import { action } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { __RouterContext } from 'react-router';
import { Link } from 'react-router-dom';
import ResizeObserver from 'resize-observer-polyfill';
import { Subject } from 'rxjs';
import { DATE_FORMAT_SHORTER } from '../../../config';
import { DEBTOR, DebtorRouteParams } from '../../../routes';
import { Case, PortalUser, User } from '../../../server-api/model';
import { caseContext } from '../../../state/caseState';
import { miscContext } from '../../../state/miscState';
import { caseSelectedSubject } from '../../../state/rxjs';
import { useSharedFlatlistStore } from '../../searchresults/debtorview/FlatList';
import { Bookmark } from '../bookmark/Bookmark';
import { Checkbox } from '../checkbox/Checkbox';
import { Chevron } from '../icons';
import VisibilitySensor from 'react-visibility-sensor';


export const windowYOffsetSubject = new Subject<number>();

interface TableRowWrapperProps {
  item: any;
  cellsToShow?: {};
  cellsHeaders: Record<string, string>;
  expansion?: JSX.Element;
  onClick?: (e: React.FormEvent<HTMLDivElement>) => void;
  onClickLink?: (e: React.FormEvent<HTMLAnchorElement>) => void;
  bookmarks?: boolean;
  selectionPool: Map<any, any>;
  link?: string;
  compactViewMode?: boolean;
  colSpan?: number;
}

export const TableRowWrapper: FC<TableRowWrapperProps> = observer((props) => {
  const sharedFlatlistStore = useSharedFlatlistStore();
  const [isClosing, setClosing] = useState(false);
  const [isOpened, setOpened] = useState(
    sharedFlatlistStore[props.item.id]?.expanded || false
  );
  const isOpenedRef = useRef(isOpened);
  const [expansionHeight, setExpansionHeight] = useState<number | 'auto'>(
    sharedFlatlistStore[props.item.id]?.height || 0
  );

  const desktopRowRef = useRef<HTMLTableRowElement>(null);
  const mobileRowRef = useRef<HTMLDivElement>(null);

  const desktopExpansionRef = useRef<HTMLDivElement>(null);
  const mobileExpansionRef = useRef<HTMLDivElement>(null);

  const expansionTransitionTimeout = useRef<NodeJS.Timeout | null>(null);

  const caseState = useContext(caseContext);
  const routerState = useContext(__RouterContext);

  const { item, cellsToShow, cellsHeaders, bookmarks, selectionPool, link, expansion, compactViewMode, colSpan } =
    props;

  const openExpansion = () => {
    if (expansionTransitionTimeout.current) {
      clearTimeout(expansionTransitionTimeout.current);
    }
    setOpened(true);
    setClosing(false);
    setExpansionHeight('auto');
  };

  const closeExpansion = () => {
    const currentExpansionRef = compactViewMode
      ? mobileExpansionRef.current
      : desktopExpansionRef.current;

    if (currentExpansionRef) {
      const height = currentExpansionRef.scrollHeight;
      setExpansionHeight(height);
      sharedFlatlistStore[props.item.id] = {
        expanded: false,
        height: 0,
      };

      requestAnimationFrame(() => {
        setExpansionHeight(0);
        setClosing(true);
        expansionTransitionTimeout.current = setTimeout(() => {
          setOpened(false);
          setClosing(false);
        }, 500);
      });
    } else {
      setExpansionHeight(0);
      sharedFlatlistStore[props.item.id] = {
        expanded: false,
        height: 0,
      };
      setClosing(true);
      expansionTransitionTimeout.current = setTimeout(() => {
        setOpened(false);
        setClosing(false);
      }, 500);
    }
  };

  useLayoutEffect(() => {
    isOpenedRef.current = isOpened;
  }, [isOpened]);

  const redirectToDebtorCase = () => {
    const { debtorId } = routerState.match.params as DebtorRouteParams;
    if (item.id === caseState.focusedCase) {
      routerState.history.push(DEBTOR + '/' + debtorId + '/cases');
    } else {
      routerState.history.push(DEBTOR + '/' + debtorId + '/cases/' + item.id);
    }
  };

  const isCase = 'debtor' in item;

  useLayoutEffect(() => {
    // if row is case, isOpened is controlled by case id url param presence,
    // and clicking the row toggles the case id url param presence.
    if (isCase && item.id === caseState.focusedCase) {
      openExpansion();
      scrollToCase();
    } else if (isOpenedRef.current) {
      closeExpansion();
    }
  }, [caseState.focusedCase]);

  const scrollToCase = () => {
    if (caseState.scrolledToCase) {
      return;
    }

    caseState.setScrolledToCase(true);

    setTimeout(() => {
      const currentRowEl = compactViewMode
        ? mobileRowRef.current
        : desktopRowRef.current;

      if (currentRowEl) {
        const element = currentRowEl;
        const elementTop = element.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: elementTop,
          behavior: 'smooth',
        });
      }
    }, 1000);
  }

  const handleClick = (
    e: React.FormEvent<HTMLAnchorElement | HTMLDivElement>
  ) => {
    e.stopPropagation();
    if (!expansion) {
      return;
    }
    e.preventDefault();
    if (isCase) {
      redirectToDebtorCase();
    } else if (isOpened && !isClosing) {
      closeExpansion();
    } else {
      openExpansion();
    }
  };

  const toggleSelectThing = action(
    (e: React.FormEvent<HTMLInputElement>, item: Case | User) => {
      selectItem(item);
    }
  );

  const itemBorderClick = (
    e: React.FormEvent<HTMLAnchorElement | HTMLDivElement>
  ) => {
    e.stopPropagation();
    selectItem(item);
  };

  const selectItem = (item: Case | User) => {
    if ('debtor' in item) {
      caseSelectedSubject.next(item.debtor.id);
    }
    if (selectionPool.get(item)) {
      selectionPool.delete(item);
    } else {
      selectionPool.set(item, true);
    }
  }

  useEffect(() => {
    // watch expansion resize and update height (only needed for dynamic content changes)
    const currentExpansionRef = compactViewMode
      ? mobileExpansionRef.current
      : desktopExpansionRef.current;

    if (!currentExpansionRef || !isOpened || isClosing) {
      return;
    }

    let resizeTimeout: NodeJS.Timeout | null = null;

    const observer = new ResizeObserver(() => {
      if (!currentExpansionRef || !isOpened || isClosing || expansionHeight !== 'auto') {
        return;
      }

      // Only observe when height is 'auto' - for dynamic content changes
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        // Keep it as 'auto' to allow dynamic resizing
        if (currentExpansionRef && expansionHeight === 'auto') {
          const height = currentExpansionRef.scrollHeight;
          sharedFlatlistStore[props.item.id] = {
            expanded: true,
            height: height,
          };
        }
      }, 50);
    });

    observer.observe(currentExpansionRef);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      observer.disconnect();
    };
  }, [isOpened, isClosing, compactViewMode, expansionHeight, props.item.id, sharedFlatlistStore]);

  const miscState = useContext(miscContext);

  if (compactViewMode) {
    return (
      <div
        className={
          'row-compact' +
          (isOpened && !isClosing ? ' expanded' : '') +
          (expansion ? ' cursor-pointer' : '')
        }
        onClick={handleClick}
        ref={mobileRowRef}
      >
        <div className='row-compact-main'>
          {!!expansion &&
            <button className='chevron-btn'
              aria-label={isOpened && !isClosing ? 'Expand' : 'Collapse'}
              aria-expanded={isOpened && !isClosing}
              aria-controls={'row-' + item.id}>
              <Chevron className='row-chevron' />
            </button>
          }
          <CompactTable
            cellsHeaders={cellsHeaders}
            cellsToShow={cellsToShow}
            item={item}
            link={link}
            handleClick={handleClick}
          />
          <Checkbox
            name={item.id}
            checked={selectionPool.has(item)}
            onChange={(e) => toggleSelectThing(e, item as any)}
            aria-label={'Select ' + getItemName(item)}
          />
          {bookmarks &&
            <Bookmark
              ariaLabel={'Bookmark ' + getItemName(item)}
              checked={(() => {
                if ('id' in item) {
                  return miscState.bookmarks.get(item.id) ? true : false;
                }
                return false;
              })()}
              className={miscState.currentlySyncing.get(item.id) ? 'progress' : ''}
              onClick={(isChecked: boolean) =>
                miscState.toggleBookmarkThing(isChecked, item as any)
              }
            />}
        </div>
        {(isOpened || isClosing) && (
          <div
            className='expansion'
            ref={mobileExpansionRef}
            style={{
              height: typeof expansionHeight === 'number' ? expansionHeight + 'px' : 'auto',
            }}
          >
            {expansion}
          </div>
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <>
      <tr
        className={
          'row spaced' +
          (isOpened && !isClosing ? ' expanded' : '') +
          (expansion ? ' cursor-pointer' : '')
        }
        onClick={handleClick}
        ref={desktopRowRef}
      >
        <TableRow
          selectionPool={selectionPool}
          showChevron={!!expansion}
          showBookmark={bookmarks}
          item={item}
          link={link}
          cellsToShow={cellsToShow}
          handleClick={handleClick}
          handleCheck={toggleSelectThing}
          expanded={isOpened && !isClosing}
        />
      </tr>
      {expansion && (
        <tr
          className={
            'expansion-wrapper' + (isOpened || isClosing ? ' expanded' : '')
          }
          id={'row-' + item.id}
        >
          <td className='expansion__content' colSpan={colSpan}>
            {(isOpened || isClosing) &&
              <div
                ref={desktopExpansionRef}
                className='expansion'
                style={{
                  height: typeof expansionHeight === 'number' ? expansionHeight + 'px' : 'auto',
                }}
              >
                <div className='expansion-border-left' />
                <VisibilitySensor
                  delayedCall={true}
                  partialVisibility={true}
                >
                  {({ isVisible }) => (
                    <div className={`expansion-content ${isVisible ? 'fade-in' : ''}`}>
                      {expansion}
                    </div>
                  )}
                </VisibilitySensor>
                <div
                  className={
                    'expansion-border-right cursor-pointer' + (selectionPool.has(item) ? ' green' : '')
                  }
                  onClick={itemBorderClick} />
              </div>
            }
          </td>
        </tr>
      )}
    </>
  );
});

interface TableRowProps {
  item: { id: string };
  cellsToShow?: {};
  showChevron?: boolean;
  showBookmark?: boolean;
  selectionPool: Map<any, any>;
  handleClick: (e: React.FormEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  handleCheck: (e: React.FormEvent<HTMLInputElement>, item: Case | User) => void;
  link?: string;
  expanded?: boolean;
}

interface CompactTableProps {
  cellsHeaders: Record<string, string>;
  cellsToShow: any;
  item: any;
  handleClick: (e: React.FormEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  link?: string;
}

const CompactTable = ({ cellsHeaders, cellsToShow, item, handleClick, link }: CompactTableProps) => {

  const renderCell = (key: string, value: any) => {
    if (key === 'caseRef' && link) {
      return (
        <td>
          <Link onClick={handleClick} to={link} className='compact-table-link'>
            {value}
          </Link>
        </td>
      );
    } else if (key === 'status') {
      return (
        <td className={'status-' + getStatusColor(value)}>
          {value}
        </td>
      );
    }

    return (
      <td>{value}</td>
    );
  };

  return (
    <table aria-label={getItemName(item)} className='CompactTable'>
      <tbody>
        {Object.entries(cellsToShow).map(([key, value]: [string, any]) => (
          <tr key={'compact-row-' + key + '-' + item.id}>
            <th scope='row'>{cellsHeaders[key] + ':'}</th>
            {renderCell(key, value)}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const TableRow = observer(
  ({
    showChevron,
    item,
    cellsToShow,
    showBookmark,
    selectionPool,
    handleClick,
    handleCheck,
    link,
    expanded,
  }: TableRowProps) => {
    const miscState = useContext(miscContext);

    const ChevronCell = (
      <td className='cell-chevron'>
        <div className='cell-border-left' />
        <button key='key-chevron' aria-label={expanded ? 'Expand' : 'Collapse'} aria-expanded={expanded} aria-controls={'row-' + item.id}>
          <Chevron className='row-chevron' />
        </button>
      </td>
    );

    const BookmarkCell = (
      <td key='key-bookmark' className={'cell-bookmark'}>
        {!showChevron && <div className='cell-border-left' />}
        <Bookmark
          ariaLabel={'Bookmark ' + getItemName(item)}
          checked={(() => {
            if ('id' in item) {
              return miscState.bookmarks.get(item.id) ? true : false;
            }
            return false;
          })()}
          className={miscState.currentlySyncing.get(item.id) ? 'progress' : ''}
          onClick={(isChecked: boolean) =>
            miscState.toggleBookmarkThing(isChecked, item as any)
          }
        />
      </td>
    );

    const CheckboxCell = (
      <td key='key-checkbox' className='cell-checkbox'>
        <Checkbox
          name={item.id}
          checked={selectionPool.has(item)}
          onChange={(e) => handleCheck(e, item as any)}
          aria-label={'Select ' + getItemName(item)}
        />
      </td>
    );

    let cells: {} = item;
    if (cellsToShow) {
      cells = cellsToShow;
    }
    return (
      <>
        {showChevron ? ChevronCell : null}
        {showBookmark ? BookmarkCell : null}
        {Object.entries(cells).map(([key, value]: [string, any]) => {
          if (key.toLowerCase().indexOf('date') > -1) {
            return (
              <td
                key={'key-' + key}
                className={
                  'cell-' + key.toString().toLowerCase().replace(/\s/g, '')
                }
              >
                {moment(value).format(DATE_FORMAT_SHORTER)}
              </td>
            );
          } else if ((key === 'caseRef' || key === 'fullname') && link) {
            return (
              <td
                key={'key-' + key}
                className={
                  'cell-' + key.toString().toLowerCase().replace(/\s/g, '')
                }
              >
                <Link
                  key={'link-key-' + key}
                  onClick={handleClick}
                  to={link}
                >
                  {value}
                </Link>
              </td>
            );
          } else if (key === 'status') {
            return (
              <td
                key={'key-' + key}
                className={
                  'cell-' +
                  key.toString().toLowerCase().replace(/\s/g, '') +
                  ' status-' +
                  getStatusColor(value)
                }
              >
                {value}
              </td>
            );
          }
          return (
            <td
              key={'key-' + key}
              className={
                'cell-' + key.toString().toLowerCase().replace(/\s/g, '')
              }
            >
              <span>{value === true ? 'Yes' : value === false ? 'No' : value}</span>
            </td>
          );
        })}
        {CheckboxCell}
      </>
    );
  }
);

const getStatusColor = (val: string) => {
  switch (val) {
    case 'Arrangement':
      return 'orange';
    case 'Live':
      return 'green';
    default:
      return 'black';
  }
};

function getItemName(item: any) {
  return ('debtor' in item) ? 'case ' + (item as Case).clientReference : 'user ' + (item as PortalUser).login;
}